import { z } from 'zod';

/**
 * RSVP typed contract (spec 0001).
 *
 * `zRsvpInput` is what a guest submits and is validated at the API boundary.
 * `zRsvpRecord` is the full persisted item: the input plus server-owned identity
 * and audit fields that come from the validated JWT — never from the request body.
 */

// Fields a guest controls. Shared by both the input and the persisted record.
const guestControlledShape = {
  attending: z.enum(['yes', 'no']),
  guestCount: z.number().int().min(0).max(10),
  dietary: z.string().trim().max(280).default(''),
  note: z.string().trim().max(500).default(''),
} as const;

// A guest who is attending must bring at least one person. Applied to both schemas.
const requireGuestWhenAttending = (
  value: { attending: 'yes' | 'no'; guestCount: number },
  ctx: z.RefinementCtx,
): void => {
  if (value.attending === 'yes' && value.guestCount < 1) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ['guestCount'],
      message: 'At least one guest is required when attending.',
    });
  }
};

/** What the client may send. `.strict()` rejects unknown keys so identity cannot be smuggled in via the body. */
export const zRsvpInput = z
  .object(guestControlledShape)
  .strict()
  .superRefine(requireGuestWhenAttending);

export type RsvpInput = z.infer<typeof zRsvpInput>;

/** The full persisted item: guest-controlled fields + server-set identity/audit fields. */
export const zRsvpRecord = z
  .object({
    ...guestControlledShape,
    cognitoSub: z.string().min(1),
    name: z.string().min(1),
    email: z.string().email(),
    updatedAt: z.string().datetime(),
  })
  .superRefine(requireGuestWhenAttending);

export type RsvpRecord = z.infer<typeof zRsvpRecord>;

/** Validate and normalise an untrusted request body into a typed `RsvpInput` (throws on invalid). */
export const parseRsvpInput = (data: unknown): RsvpInput => zRsvpInput.parse(data);
